import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { format } from 'date-fns';
import { CalendarIcon, Save, Phone } from 'lucide-react';
import { toast } from 'sonner';

const OptIntField = ({ label, name, value, onChange, helper }) => (
  <div className="space-y-1">
    <Label className="text-slate-700 text-sm font-medium">
      {label} <span className="text-slate-400 text-xs">(optional)</span>
    </Label>
    <Input type="number" min="0"
      value={value === '' || value === null || value === undefined ? '' : value}
      onChange={e => onChange(name, e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
      className="bg-white" data-testid={`cc-eod-field-${name}`} />
    {helper && <p className="text-xs text-slate-400">{helper}</p>}
  </div>
);

export default function EODCallCenterPage() {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [dropdowns, setDropdowns] = useState({});
  const [loading, setLoading] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [form, setForm] = useState({
    submission_date: format(new Date(), 'yyyy-MM-dd'),
    location_id: '',
    form_type: 'call_center',
    submitted_by: '',
    inbound_calls: '', calls_answered: '', appointments_scheduled: '',
    total_consults_scheduled: '', total_consults_showed: '',
    full_arch_cases_presented: '', non_full_arch_cases_presented: '',
    closed_today_from_today: 0, full_arch_sold_today_from_today: 0,
    arches_sold_today_from_today: 0, total_treatments_sold_today_from_today: 0,
    closed_from_previous: 0, full_arch_sold_from_previous: 0,
    arches_sold_from_previous: 0, non_full_arch_from_previous: 0,
    total_treatments_sold_from_previous: 0,
    total_collections_today: 0, surgical_production_today: 0,
    notes: ''
  });

  useEffect(() => {
    axios.get(`${API}/locations`)
      .then(r => {
        const locs = r.data.filter(l => l.active);
        setLocations(locs);
      }).catch(() => {});
    axios.get(`${API}/dropdowns`).then(r => setDropdowns(r.data)).catch(() => {});
  }, []);

  const setField = (name, val) => setForm(prev => ({ ...prev, [name]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.location_id) { toast.error('Please select a location'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/eod`, {
        ...form,
        inbound_calls: parseInt(form.inbound_calls) || 0,
        calls_answered: parseInt(form.calls_answered) || 0,
        appointments_scheduled: parseInt(form.appointments_scheduled) || 0,
        total_consults_scheduled: parseInt(form.total_consults_scheduled) || 0,
        total_consults_showed: parseInt(form.total_consults_showed) || 0,
        full_arch_cases_presented: parseInt(form.full_arch_cases_presented) || 0,
        non_full_arch_cases_presented: parseInt(form.non_full_arch_cases_presented) || 0,
        total_collections_today: 0, surgical_production_today: 0,
      });
      toast.success('Call Center EOD submitted!');
      navigate('/eod/records');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit');
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Phone size={20} className="text-cyan-400" />
          <h1 className="font-heading text-2xl font-semibold text-slate-900">EOD Summary — Call Center</h1>
        </div>
        <p className="text-slate-400 text-sm">Track daily phone activity and appointment scheduling. All fields optional.</p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-5">
        <Card className="bg-white shadow-sm">
          <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Date & Location</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-slate-700 text-sm font-medium">Date <span className="text-red-500">*</span></Label>
                <Popover open={dateOpen} onOpenChange={setDateOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left">
                      <CalendarIcon size={13} className="mr-2" />
                      {form.submission_date ? format(new Date(form.submission_date + 'T00:00:00'), 'MMMM d, yyyy') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white">
                    <Calendar mode="single"
                      selected={form.submission_date ? new Date(form.submission_date + 'T00:00:00') : undefined}
                      onSelect={d => { if (d) { setField('submission_date', format(d, 'yyyy-MM-dd')); setDateOpen(false); } }} />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-1">
                <Label className="text-slate-700 text-sm font-medium">Location <span className="text-red-500">*</span></Label>
                <Select value={form.location_id} onValueChange={v => setField('location_id', v)}>
                  <SelectTrigger><SelectValue placeholder="Select location..." /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {(dropdowns.call_center_staff?.length > 0) && (
                <div className="space-y-1">
                  <Label className="text-slate-700 text-sm font-medium">Submitted By <span className="text-slate-400 text-xs">(optional)</span></Label>
                  <Select value={form.submitted_by || '__none__'} onValueChange={v => setField('submitted_by', v === '__none__' ? '' : v)}>
                    <SelectTrigger><SelectValue placeholder="Select staff member..." /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="__none__">— Not specified —</SelectItem>
                      {(dropdowns.call_center_staff || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-sm" style={{ borderLeft: '3px solid hsl(191,95%,43%)' }}>
          <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Phone & Scheduling</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <OptIntField label="Inbound Calls" name="inbound_calls" value={form.inbound_calls} onChange={setField} />
              <OptIntField label="Calls Answered" name="calls_answered" value={form.calls_answered} onChange={setField} />
              <OptIntField label="Appointments Scheduled" name="appointments_scheduled" value={form.appointments_scheduled} onChange={setField} />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-sm">
          <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Consult Tracking <span className="text-slate-400 text-xs font-normal">(optional)</span></CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <OptIntField label="Consults Scheduled" name="total_consults_scheduled" value={form.total_consults_scheduled} onChange={setField} />
              <OptIntField label="Consults Showed" name="total_consults_showed" value={form.total_consults_showed} onChange={setField} />
              <OptIntField label="FA Presented" name="full_arch_cases_presented" value={form.full_arch_cases_presented} onChange={setField} />
              <OptIntField label="Non-FA Presented" name="non_full_arch_cases_presented" value={form.non_full_arch_cases_presented} onChange={setField} />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading">Notes for the Day</CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              Mark if office was closed, doctor was out, staff shortages, or any other context
            </p>
          </CardHeader>
          <CardContent>
            <Textarea value={form.notes} onChange={e => setField('notes', e.target.value)}
              placeholder="e.g., 'Office closed early' or 'Staff training - limited call coverage'"
              className="bg-white min-h-[70px]" />
          </CardContent>
        </Card>

        <div className="flex gap-3 pb-6">
          <Button type="submit" disabled={loading} className="gap-2 font-semibold active:scale-[0.98]"
            style={{ background: 'hsl(191,95%,43%)', color: 'hsl(222,47%,11%)' }}>
            <Save size={14} /> {loading ? 'Submitting...' : 'Submit Call Center EOD'}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
        </div>
      </form>
    </div>
  );
}
